import React, { useState, useEffect } from 'react';
import { 
  X, Users, MessageSquare, Calendar, Crown,
  Edit2, Save, Trash2, UserCheck, Star,
  Clock, Activity, UserPlus, ExternalLink
} from 'lucide-react';
import { Modal, Button, Input, LoadingSpinner, ProfileAvatar } from '../ui';
import ConfirmationModal from './ConfirmationModal';
import { toast } from '../ui/Toaster';
import { adminGroupService } from '../../services/adminGroupService';
import { getImageUrl } from '../../utils/imageUtils';

interface AdminGroupDetail {
  id: number;
  name: string;
  description: string | null;
  maxMembers: number;
  currentMembers: number;
  featuredImage: string | null;
  createdAt: string;
  updatedAt: string;
  owner: {
    id: number;
    username: string;
    email: string;
    profilePhoto: string | null;
  };
  event: {
    id: number;
    title: string;
    slug: string;
    eventType: string;
    startDate: string;
    status: string;
  } | null;
  premiumFeatures: {
    extendedSize: boolean;
    premiumExpiresAt: string | null;
  };
  statistics: {
    totalMessages: number;
    activeMembers: number;
    pendingMembers: number;
    lastMessageAt: string | null;
    averageMessagesPerDay: number;
  };
  members: Array<{
    id: number;
    user: {
      id: number;
      username: string;
      email: string;
      profilePhoto: string | null;
      createdAt: string;
    };
    status: 'ACCEPTED' | 'PENDING' | 'REJECTED';
    joinedAt: string | null;
    messageCount: number;
  }>;
  recentActivity: Array<{
    type: 'message_sent' | 'member_joined';
    timestamp: string;
    user: string;
    details: string;
  }>;
}

interface GroupDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  groupId: string;
  onGroupUpdated?: () => void;
}

export const GroupDetailModal: React.FC<GroupDetailModalProps> = ({
  isOpen,
  onClose,
  groupId,
  onGroupUpdated
}) => {
  const [groupDetail, setGroupDetail] = useState<AdminGroupDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editLoading, setEditLoading] = useState(false);

  // Edit form state
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    maxMembers: 0,
    featuredImage: ''
  });
  const [newImage, setNewImage] = useState<File | null>(null);
  const [removeImage, setRemoveImage] = useState(false);

  // Owner transfer state
  const [ownershipModal, setOwnershipModal] = useState<{
    isOpen: boolean;
    loading: boolean;
    selectedMemberId: number | null;
  }>({
    isOpen: false,
    loading: false,
    selectedMemberId: null
  });

  // Delete modal state
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    loading: boolean;
  }>({
    isOpen: false,
    loading: false
  });

  useEffect(() => {
    if (isOpen && groupId) {
      loadGroupDetail();
    }
  }, [isOpen, groupId]);

  const loadGroupDetail = async () => {
    try {
      setLoading(true);
      const detail = await adminGroupService.getGroupDetail(parseInt(groupId));
      setGroupDetail(detail);
      
      // Initialize edit form
      setEditForm({
        name: detail.name,
        description: detail.description || '',
        maxMembers: detail.maxMembers,
        featuredImage: detail.featuredImage || ''
      });
    } catch (error: any) {
      console.error('Failed to load group detail:', error);
      toast.error('Fout', 'Kon groepdetails niet laden');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSave = async () => {
    if (!groupDetail) return;

    try {
      setEditLoading(true);
      
      // Determine if we need to use FormData (for file uploads)
      const hasFileUpload = newImage || removeImage;
      
      
      if (hasFileUpload) {
        // Use FormData for file uploads
        const formData = new FormData();
        formData.append('name', editForm.name.trim());
        formData.append('description', editForm.description.trim());
        formData.append('maxMembers', editForm.maxMembers.toString());
        
        // Handle image changes
        if (removeImage) {
          formData.append('removeImage', 'true');
        } else if (newImage) {
          formData.append('featuredImage', newImage);
        }
        
        await adminGroupService.updateGroupWithFile(groupDetail.id, formData);
      } else {
        // Use regular JSON for text-only updates
        await adminGroupService.updateGroup(groupDetail.id, {
          name: editForm.name.trim(),
          description: editForm.description.trim() || null,
          maxMembers: editForm.maxMembers,
          featuredImage: editForm.featuredImage.trim() || null
        });
      }
      toast.success('Succes', 'Groep bijgewerkt');
      setIsEditing(false);
      setNewImage(null);
      setRemoveImage(false);
      
      // Clear file inputs
      if ((window as any).replaceGroupImageInput) {
        (window as any).replaceGroupImageInput.value = '';
      }
      if ((window as any).addGroupImageInput) {
        (window as any).addGroupImageInput.value = '';
      }
      if ((window as any).newGroupImageInput) {
        (window as any).newGroupImageInput.value = '';
      }
      
      // Reload group data
      await loadGroupDetail();
      
      // Notify parent component
      onGroupUpdated?.();
    } catch (error: any) {
      console.error('Failed to update group:', error);
      toast.error('Fout', error.message || 'Kon groep niet bijwerken');
    } finally {
      setEditLoading(false);
    }
  };

  const handleOwnershipTransfer = async () => {
    if (!groupDetail || !ownershipModal.selectedMemberId) return;

    try {
      setOwnershipModal(prev => ({ ...prev, loading: true }));
      
      await adminGroupService.transferOwnership(groupDetail.id, ownershipModal.selectedMemberId);
      
      toast.success('Succes', 'Eigenaarschap overgedragen');
      setOwnershipModal({ isOpen: false, loading: false, selectedMemberId: null });
      
      // Reload group data
      await loadGroupDetail();
      onGroupUpdated?.();
    } catch (error: any) {
      console.error('Failed to transfer ownership:', error);
      toast.error('Fout', error.message || 'Kon eigenaarschap niet overdragen');
      setOwnershipModal(prev => ({ ...prev, loading: false }));
    }
  };

  const handleDeleteGroup = async () => {
    if (!groupDetail) return;

    try {
      setDeleteModal(prev => ({ ...prev, loading: true }));
      
      await adminGroupService.deleteGroup(groupDetail.id);
      
      toast.success('Succes', 'Groep succesvol verwijderd');
      setDeleteModal({ isOpen: false, loading: false });
      
      // Close modal and notify parent
      onClose();
      onGroupUpdated?.();
    } catch (error: any) {
      console.error('Failed to delete group:', error);
      toast.error('Fout', error.message || 'Kon groep niet verwijderen');
      setDeleteModal(prev => ({ ...prev, loading: false }));
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('nl-NL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACCEPTED': return 'sf-badge-lime';
      case 'PENDING': return 'sf-badge-cyan';
      case 'REJECTED': return 'sf-badge-pink';
      default: return 'inline-flex items-center rounded-lg bg-white/[.06] px-2.5 py-1 font-mono text-[11px] font-semibold uppercase text-fg-body';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'ACCEPTED': return 'Geaccepteerd';
      case 'PENDING': return 'In afwachting';
      case 'REJECTED': return 'Afgewezen';
      default: return status;
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <div className="flex items-center justify-between p-6 border-b border-white/[.08]">
          <div className="flex items-center space-x-3">
            <Users className="h-6 w-6 text-neon-cyan" />
            <h2 className="text-xl font-bold text-fg-bright">
              {loading ? 'Laden...' : `Groepdetails - ${groupDetail?.name}`}
            </h2>
          </div>
          <div className="flex items-center space-x-2">
            {!loading && groupDetail && (
              <>
                {isEditing ? (
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setIsEditing(false);
                        setEditForm({
                          name: groupDetail.name,
                          description: groupDetail.description || '',
                          maxMembers: groupDetail.maxMembers,
                          featuredImage: groupDetail.featuredImage || ''
                        });
                      }}
                      disabled={editLoading}
                    >
                      Annuleren
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleEditSave}
                      disabled={editLoading}
                      className="flex items-center space-x-1"
                    >
                      {editLoading ? (
                        <LoadingSpinner size="sm" />
                      ) : (
                        <Save className="h-4 w-4" />
                      )}
                      <span>Opslaan</span>
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsEditing(true)}
                    className="flex items-center space-x-1"
                  >
                    <Edit2 className="h-4 w-4" />
                    <span>Bewerken</span>
                  </Button>
                )}
              </>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/[.06] rounded-full transition-colors"
            >
              <X className="h-5 w-5 text-fg-muted" />
            </button>
          </div>
        </div>

        <div className="p-6 max-h-[calc(90vh-120px)] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          ) : groupDetail ? (
            <div className="space-y-6">
              {/* Group Header */}
              <div className="sf-item p-4">
                {/* Featured Image Preview - Always visible */}
                {groupDetail.featuredImage && !isEditing && (
                  <div className="mb-4">
                    <p className="text-sm text-fg-muted mb-2">Uitgelichte afbeelding:</p>
                    <img
                      src={getImageUrl(groupDetail.featuredImage) || ''}
                      alt={`${groupDetail.name} featured image`}
                      className="w-48 h-24 object-cover rounded-lg border border-white/10"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </div>
                )}
                
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      {isEditing ? (
                        <Input
                          value={editForm.name}
                          onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                          className="text-xl font-bold"
                          placeholder="Groepsnaam"
                        />
                      ) : (
                        <h3 className="text-xl font-bold text-fg-bright">{groupDetail.name}</h3>
                      )}
                      {groupDetail.premiumFeatures.extendedSize && (
                        <Star className="h-5 w-5 text-neon-lime" />
                      )}
                    </div>
                    
                    <div className="mb-3">
                      {isEditing ? (
                        <textarea
                          value={editForm.description}
                          onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                          placeholder="Beschrijving (optioneel)"
                          rows={2}
                          className="sf-input resize-none"
                        />
                      ) : groupDetail.description ? (
                        <p className="text-fg-body">{groupDetail.description}</p>
                      ) : (
                        <p className="text-fg-muted italic">Geen beschrijving</p>
                      )}
                    </div>

                    {/* Featured Image Field - Only in Edit Mode */}
                    {isEditing && (
                      <div className="mb-3">
                        <label className="block text-sm font-semibold text-fg-soft mb-2">
                          Uitgelichte afbeelding
                        </label>
                        
                        {/* Current Image Display */}
                        {(editForm.featuredImage || newImage) && !removeImage && (
                          <div className="sf-item mb-4 p-4">
                            <p className="text-sm text-fg-muted mb-2">
                              {newImage ? 'Nieuwe afbeelding:' : 'Huidige afbeelding:'}
                            </p>
                            <div className="flex items-center space-x-4">
                              {newImage ? (
                                <img 
                                  src={URL.createObjectURL(newImage)}
                                  alt="New group image" 
                                  className="w-32 h-16 object-cover rounded-lg border border-white/10"
                                />
                              ) : (
                                <img 
                                  src={getImageUrl(editForm.featuredImage) || ''}
                                  alt="Current group image" 
                                  className="w-32 h-16 object-cover rounded-lg border border-white/10"
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = 'none';
                                  }}
                                />
                              )}
                              <div className="flex flex-col space-y-2">
                                <input
                                  ref={(input) => {
                                    if (input) {
                                      (window as any).replaceGroupImageInput = input;
                                    }
                                  }}
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                      if (file.size > 5 * 1024 * 1024) {
                                        toast.error('Fout', 'Afbeelding moet kleiner zijn dan 5MB');
                                        return;
                                      }
                                      setNewImage(file);
                                      setRemoveImage(false);
                                    }
                                  }}
                                />
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="text-neon-cyan"
                                  onClick={() => {
                                    const input = (window as any).replaceGroupImageInput;
                                    if (input) {
                                      input.click();
                                    }
                                  }}
                                >
                                  Vervang afbeelding
                                </Button>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setRemoveImage(true);
                                    setNewImage(null);
                                  }}
                                  className="text-neon-pink-soft"
                                >
                                  Verwijder afbeelding
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* No Image State */}
                        {!editForm.featuredImage && !newImage && !removeImage && (
                          <div className="sf-dropzone mb-4 p-4 text-center">
                            <p className="text-sm text-fg-muted mb-2">Geen afbeelding</p>
                            <input
                              ref={(input) => {
                                if (input) {
                                  (window as any).addGroupImageInput = input;
                                }
                              }}
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                  if (file.size > 5 * 1024 * 1024) {
                                    toast.error('Fout', 'Afbeelding moet kleiner zijn dan 5MB');
                                    return;
                                  }
                                  setNewImage(file);
                                  setRemoveImage(false);
                                }
                              }}
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="text-neon-cyan"
                              onClick={() => {
                                const input = (window as any).addGroupImageInput;
                                if (input) {
                                  input.click();
                                }
                              }}
                            >
                              Afbeelding toevoegen
                            </Button>
                          </div>
                        )}
                        
                        {/* Image Removed State */}
                        {removeImage && (
                          <div className="mb-4 p-4 rounded-field border border-neon-pink/40 bg-neon-pink/[.08] text-center">
                            <p className="text-sm text-neon-pink-soft mb-2">Afbeelding wordt verwijderd bij opslaan</p>
                            <div className="flex justify-center space-x-2">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => setRemoveImage(false)}
                              >
                                Annuleren
                              </Button>
                              <input
                                ref={(input) => {
                                  if (input) {
                                    (window as any).newGroupImageInput = input;
                                  }
                                }}
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    if (file.size > 5 * 1024 * 1024) {
                                      toast.error('Fout', 'Afbeelding moet kleiner zijn dan 5MB');
                                      return;
                                    }
                                    setNewImage(file);
                                    setRemoveImage(false);
                                  }
                                }}
                              />
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="text-neon-cyan"
                                onClick={() => {
                                  const input = (window as any).newGroupImageInput;
                                  if (input) {
                                    input.click();
                                  }
                                }}
                              >
                                Nieuwe afbeelding toevoegen
                              </Button>
                            </div>
                          </div>
                        )}
                        
                        <p className="text-xs text-fg-muted mt-2">
                          Aanbevolen: 4:2 aspectverhouding, max 5MB
                        </p>
                      </div>
                    )}

                    <div className="flex items-center space-x-4 text-sm text-fg-muted">
                      <div className="flex items-center space-x-1">
                        <Crown className="h-4 w-4 text-neon-lime" />
                        <span>Eigenaar: {groupDetail.owner.username}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Users className="h-4 w-4 text-neon-cyan" />
                        <span>
                          {groupDetail.currentMembers}/{isEditing ? (
                            <input
                              type="number"
                              value={editForm.maxMembers}
                              onChange={(e) => setEditForm(prev => ({ ...prev, maxMembers: parseInt(e.target.value) || 0 }))}
                              min="2"
                              max="50"
                              className="w-14 inline-block rounded-lg border border-white/10 bg-white/[.04] p-1 text-center text-fg focus:border-neon-pink/40 focus:outline-none"
                            />
                          ) : groupDetail.maxMembers} leden
                        </span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Calendar className="h-4 w-4 text-neon-cyan" />
                        <span>Aangemaakt: {formatDate(groupDetail.createdAt)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Action buttons */}
                  {!isEditing && (
                    <div className="flex flex-col space-y-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setOwnershipModal({ isOpen: true, loading: false, selectedMemberId: null })}
                        className="flex items-center space-x-1"
                      >
                        <Crown className="h-4 w-4" />
                        <span>Overdragen</span>
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => setDeleteModal({ isOpen: true, loading: false })}
                        className="flex items-center space-x-1"
                      >
                        <Trash2 className="h-4 w-4" />
                        <span>Verwijderen</span>
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Statistics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="sf-card p-4">
                  <div className="flex items-center space-x-2">
                    <div className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-xl bg-accept-gradient text-ink">
                      <UserCheck className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-fg-bright">{groupDetail.statistics.activeMembers}</p>
                      <p className="sf-label-mono">Actieve leden</p>
                    </div>
                  </div>
                </div>
                <div className="sf-card p-4">
                  <div className="flex items-center space-x-2">
                    <div className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-xl bg-neon-gradient text-ink">
                      <MessageSquare className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-fg-bright">{groupDetail.statistics.totalMessages}</p>
                      <p className="sf-label-mono">Totaal berichten</p>
                    </div>
                  </div>
                </div>
                <div className="sf-card p-4">
                  <div className="flex items-center space-x-2">
                    <div className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-xl bg-avatar-violet text-ink">
                      <Clock className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-fg-bright">{groupDetail.statistics.pendingMembers}</p>
                      <p className="sf-label-mono">In afwachting</p>
                    </div>
                  </div>
                </div>
                <div className="sf-card p-4">
                  <div className="flex items-center space-x-2">
                    <div className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-xl bg-avatar-pink text-ink">
                      <Activity className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-fg-bright">{groupDetail.statistics.averageMessagesPerDay}</p>
                      <p className="sf-label-mono">Berichten/dag</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Event Information */}
              {groupDetail.event && (
                <div className="sf-card p-4">
                  <h4 className="font-semibold text-fg-bright mb-3 flex items-center space-x-2">
                    <Calendar className="h-4 w-4" />
                    <span>Gekoppeld evenement</span>
                  </h4>
                  <div className="flex items-center justify-between">
                    <div>
                      <h5 className="font-medium text-fg-bright">{groupDetail.event.title}</h5>
                      <div className="flex items-center space-x-4 mt-1 text-sm text-fg-muted">
                        <span>Type: {groupDetail.event.eventType}</span>
                        <span>Start: {formatDate(groupDetail.event.startDate)}</span>
                        <span className={`${getStatusColor(groupDetail.event.status)}`}>
                          {groupDetail.event.status}
                        </span>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => groupDetail.event && window.open(`/events/${groupDetail.event.slug}`, '_blank')}
                      className="flex items-center space-x-1"
                    >
                      <ExternalLink className="h-4 w-4" />
                      <span>Bekijk evenement</span>
                    </Button>
                  </div>
                </div>
              )}

              {/* Premium Features */}
              <div className="sf-card p-4">
                <h4 className="font-semibold text-fg-bright mb-3 flex items-center space-x-2">
                  <Star className="h-4 w-4" />
                  <span>Premium functies</span>
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-fg-muted">Uitgebreide grootte:</span>
                    <span className={
                      groupDetail.premiumFeatures.extendedSize
                        ? 'sf-badge-lime'
                        : 'inline-flex items-center rounded-lg bg-white/[.06] px-2.5 py-1 font-mono text-[11px] font-semibold uppercase text-fg-muted'
                    }>
                      {groupDetail.premiumFeatures.extendedSize ? 'Actief' : 'Niet actief'}
                    </span>
                  </div>
                  {groupDetail.premiumFeatures.premiumExpiresAt && (
                    <div className="flex justify-between">
                      <span className="text-fg-muted">Verloopt op:</span>
                      <span className="font-medium text-fg">{formatDate(groupDetail.premiumFeatures.premiumExpiresAt)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Members */}
              <div className="sf-card p-4">
                <h4 className="font-semibold text-fg-bright mb-3 flex items-center space-x-2">
                  <Users className="h-4 w-4" />
                  <span>Leden ({groupDetail.members.length})</span>
                </h4>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {groupDetail.members.map((member) => (
                    <div key={member.id} className="sf-item flex items-center justify-between p-3">
                      <div className="flex items-center space-x-3">
                        <ProfileAvatar user={member.user} size="sm" />
                        <div>
                          <div className="flex items-center space-x-2">
                            <span className="font-medium text-fg-bright">{member.user.username}</span>
                            {member.user.id === groupDetail.owner.id && (
                              <Crown className="h-4 w-4 text-neon-lime" />
                            )}
                          </div>
                          <div className="flex items-center space-x-4 text-xs text-fg-muted">
                            <span>{member.messageCount} berichten</span>
                            {member.joinedAt && (
                              <span>Lid sinds {formatDate(member.joinedAt)}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <span className={`${getStatusColor(member.status)}`}>
                        {getStatusText(member.status)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recent Activity */}
              {groupDetail.recentActivity.length > 0 && (
                <div className="sf-card p-4">
                  <h4 className="font-semibold text-fg-bright mb-3 flex items-center space-x-2">
                    <Activity className="h-4 w-4" />
                    <span>Recente activiteit</span>
                  </h4>
                  <div className="space-y-3 max-h-48 overflow-y-auto">
                    {groupDetail.recentActivity.map((activity, index) => (
                      <div key={index} className="sf-item flex items-start space-x-3 p-2">
                        <div className="flex-shrink-0">
                          {activity.type === 'message_sent' ? (
                            <MessageSquare className="h-4 w-4 text-neon-cyan mt-0.5" />
                          ) : (
                            <UserPlus className="h-4 w-4 text-neon-lime mt-0.5" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-fg-body">{activity.details}</p>
                          <p className="font-mono text-[11px] text-fg-meta">{formatDate(activity.timestamp)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="italic text-fg-muted">Groep niet gevonden</p>
            </div>
          )}
        </div>
      </Modal>

      {/* Ownership Transfer Modal */}
      <ConfirmationModal
        isOpen={ownershipModal.isOpen}
        onClose={() => !ownershipModal.loading && setOwnershipModal({ isOpen: false, loading: false, selectedMemberId: null })}
        onConfirm={handleOwnershipTransfer}
        title="Eigenaarschap overdragen"
        message={
          <div>
            <p className="mb-3">Selecteer een nieuw lid om eigenaar te maken van "{groupDetail?.name}":</p>
            <select
              value={ownershipModal.selectedMemberId || ''}
              onChange={(e) => setOwnershipModal(prev => ({ 
                ...prev, 
                selectedMemberId: e.target.value ? parseInt(e.target.value) : null 
              }))}
              className="sf-input"
            >
              <option value="">Selecteer nieuw lid</option>
              {groupDetail?.members
                .filter(member => member.status === 'ACCEPTED' && member.user.id !== groupDetail?.owner.id)
                .map(member => (
                  <option key={member.user.id} value={member.user.id}>
                    {member.user.username}
                  </option>
                ))
              }
            </select>
          </div>
        }
        confirmText="Overdragen"
        type="warning"
        loading={ownershipModal.loading}
        confirmDisabled={!ownershipModal.selectedMemberId}
      />

      {/* Delete Confirmation */}
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => !deleteModal.loading && setDeleteModal({ isOpen: false, loading: false })}
        onConfirm={handleDeleteGroup}
        title="Groep verwijderen"
        message={`Weet je zeker dat je "${groupDetail?.name}" permanent wilt verwijderen? Dit verwijdert alle berichten en kan niet ongedaan worden gemaakt.`}
        confirmText="Verwijderen"
        type="danger"
        loading={deleteModal.loading}
      />
    </>
  );
};